/*
 * Sanjog: Myntra-style sticky Add to cart on the mobile product page.
 * The theme's own sticky bar only appears after the shopper scrolls PAST the
 * main buy buttons. On phones we also show it BEFORE they are reached, so a
 * buy button is on screen from the moment the page opens. It hides while the
 * main Add to cart / Buy Now buttons are on screen, and near the footer.
 * Desktop behaviour is unchanged.
 */
(() => {
  const mq = window.matchMedia('(max-width: 749px)');
  const root = document.documentElement;

  function init() {
    const sticky = document.querySelector('sticky-add-to-cart');
    const bar = sticky?.querySelector('[ref="stickyBar"]');
    const section = sticky?.closest('.shopify-section');
    const buyBlock = section?.querySelector('.buy-buttons-block');
    if (!bar || !buyBlock) return;
    if (sticky.dataset.variantAvailable === 'false') return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const state = { buyVisible: true, footerVisible: false };

    const apply = () => {
      if (!mq.matches) {
        root.classList.remove('satc-on');
        return;
      }
      const show = !state.buyVisible && !state.footerVisible;
      bar.dataset.stuck = show ? 'true' : 'false';
      root.classList.toggle('satc-on', show);
    };

    new IntersectionObserver(([entry]) => {
      if (!entry) return;
      state.buyVisible = entry.isIntersecting;
      apply();
    }).observe(buyBlock);

    if (footer) {
      new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          state.footerVisible = entry.isIntersecting;
          apply();
        },
        { rootMargin: '200px 0px 0px 0px' }
      ).observe(footer);
    }

    // The theme component may flip the bar after its own observers run; re-assert ours on mobile.
    new MutationObserver(() => {
      if (!mq.matches) return;
      const want = !state.buyVisible && !state.footerVisible ? 'true' : 'false';
      if (bar.dataset.stuck !== want) bar.dataset.stuck = want;
    }).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });

    mq.addEventListener('change', apply);

    setupStepper(sticky, bar);
  }

  /*
   * In-cart stepper for the sticky bar: once this item is in the cart, the bar's
   * Add to cart button becomes [bin/minus]  N in cart  [+]. Uses the same markup as
   * the product-card stepper, so assets/card-actions.js handles the clicks and
   * keeps it in sync with the cart drawer and header count.
   */
  const TRASH =
    '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 5.5h13"/><path d="M8 5.5V3.8h4v1.7"/><path d="M5.3 5.5l.8 10.7h7.8l.8-10.7"/><path d="M8.3 8.5v5M11.7 8.5v5"/></svg>';
  const MINUS =
    '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4.5 10h11"/></svg>';
  const PLUS =
    '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4.5 10h11M10 4.5v11"/></svg>';

  function setupStepper(sticky, bar) {
    const ensure = () => {
      const btn = bar.querySelector('.sticky-add-to-cart__button');
      if (!btn) return null;
      let wrap = btn.closest('.satc-actions');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'card-actions satc-actions';
        btn.before(wrap);
        wrap.appendChild(btn);
        btn.classList.add('card-actions__atc');
        const qty = document.createElement('div');
        qty.className = 'card-qty';
        qty.setAttribute('role', 'group');
        qty.setAttribute('aria-label', 'Quantity in cart');
        qty.hidden = true;
        qty.innerHTML =
          '<button type="button" class="card-qty__btn card-qty__minus" aria-label="Remove from cart">' + TRASH + '</button>' +
          '<span class="card-qty__label" aria-live="polite"><span class="card-qty__count">0</span> in cart</span>' +
          '<button type="button" class="card-qty__btn card-qty__plus" aria-label="Increase quantity">' + PLUS + '</button>';
        wrap.appendChild(qty);
      }
      const vid = sticky.dataset.currentVariantId;
      if (vid) wrap.dataset.variantId = vid;
      else delete wrap.dataset.variantId;
      return wrap;
    };

    const sync = async () => {
      const wrap = ensure();
      if (!wrap || !wrap.dataset.variantId) return;
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
        const n = (cart.items || [])
          .filter((i) => String(i.variant_id) === wrap.dataset.variantId)
          .reduce((s, i) => s + i.quantity, 0);
        const qty = wrap.querySelector('.card-qty');
        wrap.classList.toggle('card-actions--in-cart', n > 0);
        qty.hidden = n <= 0;
        qty.querySelector('.card-qty__count').textContent = String(n);
        const minus = qty.querySelector('.card-qty__minus');
        minus.innerHTML = n <= 1 ? TRASH : MINUS;
        minus.setAttribute('aria-label', n <= 1 ? 'Remove from cart' : 'Decrease quantity');
      } catch (e) {}
    };

    // Re-attach after the theme re-renders the bar (variant change), and follow the selected variant.
    new MutationObserver(() => {
      if (!bar.querySelector('.satc-actions')) sync();
    }).observe(bar, { childList: true });
    new MutationObserver(sync).observe(sticky, { attributes: true, attributeFilter: ['data-current-variant-id'] });

    // After an add from the main button or the bar itself, card-actions.js re-renders on the cart event;
    // this is a fallback in case that event carries no line items.
    document.addEventListener('cart:update', () => setTimeout(sync, 300));
    document.addEventListener('shopify:cart:lines-update', (e) => {
      const later = () => setTimeout(sync, 50);
      (e.promise || Promise.resolve()).then(later, later);
    });

    sync();
  }

  if (customElements.get('sticky-add-to-cart')) init();
  else customElements.whenDefined('sticky-add-to-cart').then(init);
})();
